import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { LetDirective } from '@ngrx/component';
import { map, startWith } from 'rxjs';

import type { Issue } from '@ninety/platform/models/issues/issue';
import { TerraIconModule, TerraModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';
import { NameSearchPipe } from '@ninety/ui/legacy/shared/index';

/** Minimal team shape the picker needs — any team-like object with an id and name. */
export interface SendToTeamOption {
  _id: string;
  name: string;
}

export interface SendToTeamDialogData {
  issue: Issue;
  /** Teams the issue can be sent to — already excludes the owning team. */
  teams: SendToTeamOption[];
}

export interface SendToTeamDialogResult {
  teamId: string;
  comment: string;
}

@Component({
  selector: 'ninety-send-to-team-dialog',
  templateUrl: './send-to-team-dialog.component.html',
  styleUrls: ['./send-to-team-dialog.component.scss'],
  standalone: true,
  providers: [NameSearchPipe],
  imports: [
    ReactiveFormsModule,
    LetDirective,
    MatDialogTitle,
    MatDialogClose,
    MatDialogContent,
    MatDialogActions,
    TerraModule,
    TerraIconModule,
    ButtonComponent,
  ],
})
export class SendToTeamDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<SendToTeamDialogComponent, SendToTeamDialogResult>>(MatDialogRef);
  private readonly nameSearchPipe = inject(NameSearchPipe);
  protected readonly data = inject<SendToTeamDialogData>(MAT_DIALOG_DATA);

  protected readonly teamControl = new FormControl<string | null>(null);
  protected readonly commentControl = new FormControl<string>('', { nonNullable: true });

  protected readonly teamSearchTerm = new FormControl('');
  protected readonly teamSearchResults$ = this.teamSearchTerm.valueChanges.pipe(
    startWith(this.teamSearchTerm.value),
    map(searchTerm => (searchTerm ? this.nameSearchPipe.transform(this.data.teams, searchTerm) : []))
  );

  send(): void {
    const teamId = this.teamControl.value;
    if (!teamId) return;

    this.dialogRef.close({ teamId, comment: this.commentControl.value.trim() });
  }
}
